import { z } from 'zod';
import {
  SignalCategorySchema as GeneratedSignalCategorySchema,
  type SignalCategoryType as GeneratedSignalCategoryType,
} from '../generated';

export const SignalCategorySchema = z.enum(
  GeneratedSignalCategorySchema.options,
);

export type SignalCategoryType = z.infer<typeof SignalCategorySchema>;

const Category = GeneratedSignalCategorySchema.enum;

export const SignalCategoryGroupSchema = z.enum([
  'INTERNAL_NETWORK',
  'EXTERNAL_NETWORK',
  'WEB_APP',
  'AGENTIC_AI',
  'MCP_SERVER',
]);

export type SignalCategoryGroup = z.infer<typeof SignalCategoryGroupSchema>;

const Group = SignalCategoryGroupSchema.enum;

// ─── Registry ────────────────────────────────────────────────────────────────
// The only place that decides which group a category belongs to. Adding a
// Prisma enum value fails compilation here until it is assigned a group.

export const SIGNAL_CATEGORY_GROUP_MEMBERSHIP = {
  [Category.ACL_ABUSE]: [Group.INTERNAL_NETWORK],
  [Category.ADCS_ABUSE]: [Group.INTERNAL_NETWORK],
  [Category.AGENT_GOAL_HIJACK]: [Group.AGENTIC_AI],
  [Category.AGENTIC_SUPPLY_CHAIN]: [Group.AGENTIC_AI],
  [Category.AI_CACHE_POISONING]: [Group.AGENTIC_AI],
  [Category.ARBITRARY_FILE_READ]: [Group.WEB_APP],
  [Category.ARBITRARY_FILE_WRITE]: [Group.WEB_APP],
  [Category.AS_REP_ROASTING]: [Group.INTERNAL_NETWORK],
  [Category.BFLA]: [Group.WEB_APP],
  [Category.BOLA]: [Group.WEB_APP],
  [Category.BOPLA]: [Group.WEB_APP],
  [Category.BROKEN_ACCESS]: [Group.WEB_APP, Group.EXTERNAL_NETWORK],
  [Category.BROKEN_AUTHENTICATION]: [Group.WEB_APP],
  [Category.CODE_INJECTION]: [Group.WEB_APP],
  [Category.COMMAND_INJECTION]: [Group.WEB_APP],
  [Category.CONSTRAINED_DELEGATION]: [Group.INTERNAL_NETWORK],
  [Category.CORS]: [Group.WEB_APP],
  [Category.CSV_INJECTION]: [Group.WEB_APP],
  [Category.CSRF]: [Group.WEB_APP],
  [Category.CVE]: [
    Group.WEB_APP,
    Group.EXTERNAL_NETWORK,
    Group.INTERNAL_NETWORK,
  ],
  [Category.CASCADING_FAILURES]: [Group.AGENTIC_AI],
  [Category.DATA_EXPOSURE]: [
    Group.WEB_APP,
    Group.EXTERNAL_NETWORK,
    Group.MCP_SERVER,
  ],
  [Category.DEFAULT_CREDENTIALS]: [Group.EXTERNAL_NETWORK],
  [Category.DCSYNC]: [Group.INTERNAL_NETWORK],
  [Category.DOM_XSS]: [Group.WEB_APP],
  [Category.EMAIL_SECURITY]: [Group.EXTERNAL_NETWORK],
  [Category.EXPOSED_ADMIN_PANEL]: [Group.EXTERNAL_NETWORK],
  [Category.EXPOSED_SERVICE]: [Group.EXTERNAL_NETWORK],
  [Category.FILE_INCLUSION]: [Group.WEB_APP],
  [Category.FILE_UPLOAD]: [Group.WEB_APP],
  [Category.GPO_ABUSE]: [Group.INTERNAL_NETWORK],
  [Category.HTTP_REQUEST_SMUGGLING]: [Group.WEB_APP],
  [Category.HTTP_RESPONSE_SPLITTING]: [Group.WEB_APP],
  [Category.HUMAN_AGENT_TRUST_EXPLOITATION]: [Group.AGENTIC_AI],
  [Category.IDOR]: [Group.WEB_APP],
  [Category.IDENTITY_PRIVILEGE_ABUSE]: [
    Group.INTERNAL_NETWORK,
    Group.AGENTIC_AI,
    Group.MCP_SERVER,
  ],
  [Category.INFORMATION_DISCLOSURE]: [Group.WEB_APP, Group.EXTERNAL_NETWORK],
  [Category.INSECURE_DESERIALIZATION]: [Group.WEB_APP],
  [Category.INSECURE_INTER_AGENT_COMMUNICATION]: [Group.AGENTIC_AI],
  [Category.INSECURE_RANDOMNESS]: [Group.WEB_APP],
  [Category.INSECURE_TRANSPORT]: [Group.WEB_APP, Group.EXTERNAL_NETWORK],
  [Category.KERBEROASTING]: [Group.INTERNAL_NETWORK],
  [Category.LDAP_INJECTION]: [Group.WEB_APP],
  [Category.LOCAL_PRIVILEGE_ESCALATION]: [Group.INTERNAL_NETWORK],
  [Category.MASS_ASSIGNMENT]: [Group.WEB_APP],
  [Category.MEMORY_CONTEXT_POISONING]: [Group.AGENTIC_AI, Group.MCP_SERVER],
  [Category.NOSQL_INJECTION]: [Group.WEB_APP],
  [Category.OPEN_REDIRECT]: [Group.WEB_APP, Group.EXTERNAL_NETWORK],
  [Category.OTHER]: [Group.WEB_APP, Group.EXTERNAL_NETWORK],
  [Category.PADDING_ORACLE]: [Group.WEB_APP],
  [Category.PARAMETER_POLLUTION]: [Group.WEB_APP],
  [Category.PATH_TRAVERSAL]: [Group.WEB_APP],
  [Category.PROMPT_INJECTION]: [Group.WEB_APP, Group.MCP_SERVER],
  [Category.TOOL_POISONING]: [Group.MCP_SERVER],
  [Category.PROTOTYPE_POLLUTION]: [Group.WEB_APP],
  [Category.RACE_CONDITION]: [Group.WEB_APP],
  [Category.REFLECTED_XSS]: [Group.WEB_APP],
  [Category.ROGUE_AGENTS]: [Group.AGENTIC_AI],
  [Category.RESOURCE_BASED_DELEGATION]: [Group.INTERNAL_NETWORK],
  [Category.SERVER_MISCONFIGURATION]: [Group.WEB_APP, Group.EXTERNAL_NETWORK],
  [Category.SESSION_FIXATION]: [Group.WEB_APP],
  [Category.SMB_RELAY]: [Group.INTERNAL_NETWORK],
  [Category.SQL_INJECTION]: [Group.WEB_APP],
  [Category.SSRF]: [Group.WEB_APP, Group.EXTERNAL_NETWORK],
  [Category.SSTI]: [Group.WEB_APP],
  [Category.STORED_XSS]: [Group.WEB_APP],
  [Category.SUBDOMAIN_TAKEOVER]: [Group.EXTERNAL_NETWORK],
  [Category.TOOL_MISUSE]: [Group.AGENTIC_AI, Group.MCP_SERVER],
  [Category.UNEXPECTED_CODE_EXECUTION]: [Group.AGENTIC_AI],
  [Category.UNCONSTRAINED_DELEGATION]: [Group.INTERNAL_NETWORK],
  [Category.WEAK_CRYPTO]: [Group.WEB_APP, Group.EXTERNAL_NETWORK],
  [Category.XPATH_INJECTION]: [Group.WEB_APP],
  [Category.XXE]: [Group.WEB_APP],
  [Category.VULNERABILITY_CHAINING]: [Group.WEB_APP],
  [Category.EXCESSIVE_AGENCY]: [Group.MCP_SERVER],
} as const satisfies Record<
  GeneratedSignalCategoryType,
  readonly [SignalCategoryGroup, ...SignalCategoryGroup[]]
>;
